import type { Instrumentation } from "next";

import {
  createDatadogContext,
  sendErrorToDatadog
} from "./datadog/error-reporting";
import {
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
  captureRequestError as sentryCaptureRequestError,
  captureRouterTransitionStart as sentryCaptureRouterTransitionStart,
  init as sentryInit
} from "./sentry/error-reporting";
import type { ErrorOptions, MessageOptions } from "./types";

/**
 * Capture an exception with optional additional context
 * Sends to both Sentry and DataDog RUM
 */
export const captureException = (
  error: Error,
  options?: ErrorOptions
): string => {
  // Send to DataDog RUM
  const datadogContext = createDatadogContext(options);
  sendErrorToDatadog(error, datadogContext);

  // Send to Sentry
  return sentryCaptureException(error, options);
};

/**
 * Capture a message with optional severity level and context
 * Sends to both Sentry and DataDog RUM
 */
export const captureMessage = (
  message: string,
  options?: MessageOptions
): string => {
  // Send to DataDog RUM as an error with special context
  const datadogContext = {
    ...createDatadogContext(options),
    messageLevel: options?.level || "info",
    isMessage: true
  };
  sendErrorToDatadog(new Error(`[Message] ${message}`), datadogContext);

  // Send to Sentry
  return sentryCaptureMessage(message, options);
};

/**
 * Capture request errors (mainly for Next.js instrumentation)
 * Sends to both Sentry and DataDog RUM
 */
export const captureRequestError = async (
  error: Error,
  request: any,
  errorContext?: any
): Promise<void> => {
  // Send to DataDog RUM with request context
  const datadogContext = {
    ...errorContext,
    requestUrl: request?.url,
    requestMethod: request?.method,
    isRequestError: true
  };
  sendErrorToDatadog(error, datadogContext);

  // Send to Sentry
  sentryCaptureRequestError(error, request, errorContext);
};

/**
 * Initialize the error reporting service
 */
export const init = (config: any): void => {
  sentryInit(config);
};

// Hook for Next.js instrumentation
export const onRequestError: Instrumentation.onRequestError = async (
  error,
  request,
  errorContext
) => {
  await captureRequestError(error as Error, request, errorContext);
};
